import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch, AnyAction } from 'redux';
import { DropdownButtonType, List, ListItem, SingleSelectDropdown } from '@tableau/widgets';
import { DisplayBy, DisplaySelection } from '../models/DisplaySelection';
import { AppState } from '../../app/state/AppState';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { setDisplayMode } from '../actions/setDisplayModeAction';
import { imgPath } from '../../common/utils/ImgUrl';
import * as Layout from '../../common/styles/Layout';
import { CommonStyles } from '../../common/styles/CommonStyles';
import { TelemetryService } from '../services/TelemetryService';
import { ContentTypeFilterAllOption, ContentTypeFilterValue } from '../models/ContentTypeFilterValue';
import { Messages } from '../../../gen-src/Localize';

export type DisplayByDropdownProps = DisplayByDropdownStateProps & DisplayByDropdownDispatchProps & RouteComponentProps;

export const DisplayDropdownImageTestID = 'DisplayDropdownImage';

export function DisplayByDropdown(props: DisplayByDropdownProps): JSX.Element {
  const displayByList = [
    { display: DisplayBy.getMixedViewDisplaySelection(), path: 'list_thumbnail_mixed_odd_16px.svg' },
    { display: DisplayBy.getListViewDisplaySelection(), path: 'list_odd_16px.svg' },
  ];

  const items: ListItem[] = displayByList.map((item) => ({
    content: (
      <div className={Layout.flexH} style={{ padding: '8px', alignItems: 'center' }}>
        <div className={Layout.flexAuto} style={(CommonStyles.Layout.FlexBlockVerticalCenter, { minWidth: '80px', paddingRight: '8px' })}>
          {props.contentTypeFilterValue !== ContentTypeFilterAllOption &&
          item.display.name === DisplayBy.getMixedViewDisplaySelection().name
            ? Messages.DisplayOptionCardView()
            : item.display.primaryDisplay.content}
        </div>
        <img src={imgPath(item.path)} alt={''} />
      </div>
    ),
    value: item.display.name,
  }));

  const [displayName, setDisplayName] = React.useState(props.displayBy?.name);

  function onDropDownValueChanged(value: string) {
    const selection = displayByList.find((x) => x.display.name === value);
    setDisplayName(value);
    if (selection) {
      props.setDisplayMode(selection.display);
    }

    TelemetryService.instance.sendSearchResultClickedEvent({
      type: 'SearchResultClicked',
      eventSource: 'displayModeToggle',
      resultType: value,
      seeAll: true,
      index: 0,
      total: 0,
      searchTerm: props.searchTerm,
      contentId: props.contentTypeFilterValue,
    });
  }

  function getDisplayValue(displayNameFromState?: string) {
    const selection = displayByList.find((x) => x.display.name === displayNameFromState);
    if (displayNameFromState === DisplayBy.getMixedViewDisplaySelection().name) {
      return (
        <img
          data-tb-test-id={DisplayDropdownImageTestID}
          style={CommonStyles.Layout.FlexBlockVerticalCenter}
          src={imgPath(selection?.path || '')}
          alt={''}
        />
      );
    } else {
      return (
        <img
          data-tb-test-id={DisplayDropdownImageTestID}
          style={CommonStyles.Layout.FlexBlockVerticalCenter}
          src={imgPath(selection?.path || '')}
          alt={''}
        />
      );
    }
  }

  function getSelectedValue(displayNameFromState?: string) {
    if (displayNameFromState) {
      const displayIsAvailableForContentType = displayByList.find((x) => x.display.name === displayNameFromState);
      return displayIsAvailableForContentType ? displayIsAvailableForContentType.display.name : 'list view';
    } else {
      return 'list view';
    }
  }

  return (
    <SingleSelectDropdown
      id="search-display-dropdown"
      label=""
      list={<List items={items} />}
      type={DropdownButtonType.Line}
      onChange={(value) => onDropDownValueChanged(value)}
      value={getSelectedValue(displayName)}
    >
      {getDisplayValue(displayName)}
    </SingleSelectDropdown>
  );
}

export interface DisplayByDropdownStateProps {
  displayBy?: DisplaySelection;
  searchTerm: string;
  contentTypeFilterValue: ContentTypeFilterValue;
}

export interface DisplayByDropdownDispatchProps {
  setDisplayMode: (displaySelection: DisplaySelection) => void;
}

const mapStateToProps = (state: AppState) => ({
  displayBy: state.search.displayBy,
  searchTerm: state.search.searchTerm,
  contentTypeFilterValue: state.search.selectedContentTypeFilterValue,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      setDisplayMode: setDisplayMode,
    },
    dispatch
  );

const DisplayByDropdownWithRouter = withRouter(DisplayByDropdown);

export const ConnectedDisplayByDropdown: React.ComponentClass<{}> = connect(
  mapStateToProps,
  mapDispatchToProps
)(DisplayByDropdownWithRouter);
